import {
  fetchAgencyClientTargets,
  toggleAgencyClientTargetActivation,
} from "api/targets"
import { queryCache, useMutation, useQuery } from "react-query"

import { ClientAgencyTarget } from "models/targets"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import { StyledTable } from "components/Rable/StyledTable"
import { Switch } from "@material-ui/core"
import { css } from "@emotion/css"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSendFeedBack } from "components/FeedbackSnackbar"

type Col = {
  name: string
  cell: React.ComponentType<CellProps>
  align?: "center" | "left" | "right"
  width?: React.CSSProperties["width"]
}

type CellProps = {
  row: ClientAgencyTarget
  name: string
  update: (checked: boolean, id: string) => void
}

const cols: Col[] = [
  { name: "name", cell: ReadOnlyCell, align: "left", width: 180 },
  { name: "enabled", cell: ActionCell, align: "center", width: 80 },
]

export default function AgencyClientTargets() {
  const { t } = useI18n()
  const { configId } = useParams<{ configId: string }>()
  const feedback = useSendFeedBack()

  const { data: targets, isLoading } = useQuery(
    ["agency-client-targets", configId],
    fetchAgencyClientTargets,
  )

  const activeTargets = targets?.filter((t) => t.status === "ACTIVE")

  const [toggle] = useMutation(toggleAgencyClientTargetActivation, {
    onSuccess: () =>
      queryCache.invalidateQueries(["agency-client-targets", configId]),
    onError: (e) => {
      feedback(t(e.message), "error")
    },
  })

  const updateRow = (checked: boolean, id: string) => {
    toggle({ enabled: checked, clientAgencyConfigId: configId, targetId: id })
  }

  return (
    <div className={styles.container}>
      {isLoading ? (
        <Loading />
      ) : activeTargets?.length ? (
        <StyledTable className={styles.table}>
          <thead>
            <tr role="row">
              {cols.map((col) => (
                <th
                  key={col.name}
                  role="columnheader"
                  style={{ width: col.width }}
                >
                  <span>{t(`agency.client.targets.${col.name}`)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeTargets?.map((row, rIndex) => (
              <tr key={rIndex} role="row">
                {cols.map((col) => {
                  const { name, cell: Cell, align = "left" } = col
                  return (
                    <td className={align}>
                      <Cell row={row} name={name} update={updateRow} />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      ) : (
        <EmptyState title={t("agency.client.targets.noresults.title")} />
      )}
    </div>
  )
}

function ReadOnlyCell(props: CellProps) {
  const { row, name } = props
  return <div>{row[name as keyof ClientAgencyTarget]}</div>
}

function ActionCell(props: CellProps) {
  const { hasAuthority } = useAuthenticated()
  const { row, update } = props
  return (
    <Switch
      color="primary"
      checked={row?.enabled}
      onChange={(_, checked) => update(checked, row?.id)}
      disabled={!hasAuthority(["AUTH_EDIT_CLIENT_TARGET"])}
    />
  )
}

const styles = {
  container: css`
    padding: var(--spacing-xxl-2) 0px 0px;
  `,
  table: css`
    width: unset;
  `,
}
